import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { FLAVOURS } from "../data/flavours";
import TermsCheckbox from "../components/TermsCheckbox";
import { subscribeToOrderUpdates } from "../utils/orderStream";
import {
  Package, Ticket, User, LogOut, ArrowLeft,
  ChevronRight, Lock, Pencil, Save, X, Gift, Copy, Check, Mail, KeyRound,
  ClipboardCheck, PackageCheck, Truck, Home, AlertTriangle, ExternalLink, ShoppingBag, Star,
} from "lucide-react";

// Sign-in goes straight to the store's JWT plugin; anything needing WooCommerce write access
// (sign-up, addresses, coupons) goes through the middleware, which holds the API keys.
const WOO_API_BASE = (process.env.REACT_APP_WOO_URL || "https://store.krunchmate.com").replace(/\/+$/, "");
const MIDDLEWARE_URL = (process.env.REACT_APP_MIDDLEWARE_URL || "https://krunchmate-middleware.onrender.com").replace(/\/+$/, "");

const WELCOME_COUPON_KEY = "km_welcome_coupon";

const EMPTY_ADDRESS = { firstName: "", lastName: "", address: "", city: "", postcode: "", country: "GB" };

// localStorage can throw in private browsing or with site data blocked
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // the session still works for this page view
  }
};

// The middleware sends money as integer pence
const formatPence = (pence) => `£${(Number.isFinite(pence) ? pence / 100 : 0).toFixed(2)}`;
const formatDate = (dateString) => {
  const date = dateString ? new Date(dateString) : null;
  if (!date || Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const formatCountry = (code) => (!code || code === "GB" ? "United Kingdom" : code);
const hasAddress = (address) => Boolean(address?.firstName && address?.address);

// Only show server messages that are plain, short strings (WordPress errors can contain HTML)
const safeMessage = (value, fallback) =>
  typeof value === "string" && value.length > 0 && value.length <= 200 ? value.replace(/<[^>]*>?/gm, "") : fallback;

async function middlewareRequest(path, { method = "GET", token, body } = {}) {
  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${MIDDLEWARE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data: data && typeof data === "object" ? data : {} };
}

// Returns { token, email } or throws an Error whose message is safe to show
async function requestStoreToken(email, password) {
  const res = await fetch(`${WOO_API_BASE}/wp-json/jwt-auth/v1/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // The WP JWT plugin requires "username"; WordPress accepts an email address in it
    body: JSON.stringify({ username: email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && typeof data.token === "string" && data.token) {
    return { token: data.token, email: data.user_email || email };
  }
  throw new Error(safeMessage(data.message, "Incorrect email address or password."));
}

const inputClass =
  "w-full p-3 bg-black/40 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] outline-none text-[#FFFBE5] placeholder-[#FFFBE5]/30";
const smallInputClass =
  "w-full p-2.5 bg-black/40 border border-[#FFFBE5]/20 rounded-lg text-sm outline-none text-[#FFFBE5] focus:border-[#F6A81E]";

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const submitButtonClass =
  "w-full py-4 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#f7b43b] transition-colors disabled:opacity-50";
const NETWORK_ERROR_MESSAGE = "Server communication failed. Please check your connection and try again.";

const LoginForm = ({ onLogin, onForgotPassword, isLoading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email.trim(), password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="login-email" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Email Address</label>
        <input id="login-email" type="email" required autoComplete="email" placeholder="alex@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
      </div>
      <div>
        <label htmlFor="login-password" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Password</label>
        <input id="login-password" type="password" required autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
        <button type="button" onClick={onForgotPassword} className="mt-2 text-xs underline opacity-70 hover:opacity-100 hover:text-[#F6A81E]">
          Forgot password?
        </button>
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-4 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#f7b43b] transition-colors disabled:opacity-50"
      >
        {isLoading ? "Signing in..." : "Sign In"} <Lock size={16} />
      </button>
    </form>
  );
};

// The answer is the same whether or not the account exists
const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sentMessage, setSentMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSending(true);
    try {
      const { ok, data } = await middlewareRequest("/api/account/forgot-password", { method: "POST", body: { email: email.trim() } });
      if (ok && data.success === true) {
        setSentMessage(safeMessage(data.message, "If an account exists for that email address, a reset link has been sent."));
      } else {
        setError(safeMessage(data.error, "We couldn't send a reset link. Please try again."));
      }
    } catch {
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setIsSending(false);
    }
  };

  if (sentMessage) {
    return (
      <div className="space-y-4 text-center">
        <p role="status" className="p-3 bg-[#F6A81E]/15 border border-[#F6A81E]/40 rounded-xl text-sm">{sentMessage}</p>
        <p className="text-xs opacity-60">The link expires in 24 hours. Check your spam folder if it doesn't arrive.</p>
        <button type="button" onClick={onBack} className="text-sm underline opacity-80 hover:opacity-100">Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div role="alert" className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200 text-xs text-center">{error}</div>
      )}
      <div>
        <label htmlFor="forgot-email" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Email Address</label>
        <input id="forgot-email" type="email" required maxLength={254} autoComplete="email" placeholder="alex@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
      </div>
      <button type="submit" disabled={isSending} className={submitButtonClass}>
        {isSending ? "Sending..." : "Send Reset Link"} <Mail size={16} />
      </button>
      <button type="button" onClick={onBack} className="w-full text-sm underline opacity-80 hover:opacity-100">Back to sign in</button>
    </form>
  );
};

// onSubmit resolves to { ok, error }; the current password is asked for again before any change
const ChangePasswordForm = ({ onSubmit }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    setIsSaving(true);
    const outcome = await onSubmit(currentPassword, newPassword);
    setIsSaving(false);
    if (outcome.ok) {
      setCurrentPassword("");
      setNewPassword("");
      setResult({ ok: true, message: "Password updated successfully." });
    } else if (outcome.error) {
      setResult({ ok: false, message: outcome.error });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-display uppercase tracking-widest text-sm opacity-60">Change Password</h3>
      {result && (
        <p role={result.ok ? "status" : "alert"} className={`p-3 rounded-lg text-xs ${result.ok ? "bg-[#F6A81E]/15 border border-[#F6A81E]/40" : "bg-red-500/20 border border-red-500/40 text-red-200"}`}>
          {result.message}
        </p>
      )}
      <div>
        <label htmlFor="current-password" className="block text-xs uppercase tracking-widest opacity-60 mb-1">Current Password</label>
        <input id="current-password" type="password" required maxLength={256} autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={smallInputClass} />
      </div>
      <div>
        <label htmlFor="new-password" className="block text-xs uppercase tracking-widest opacity-60 mb-1">New Password</label>
        <input id="new-password" type="password" required minLength={8} maxLength={128} autoComplete="new-password" placeholder="At least 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={smallInputClass} />
      </div>
      <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-[#F6A81E] text-[#1D2528] rounded-lg text-sm font-bold disabled:opacity-50">
        <KeyRound size={14} /> {isSaving ? "Updating..." : "Update Password"}
      </button>
    </form>
  );
};

const RegisterForm = ({ onRegister, isLoading }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "" });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const handleChange = (e) => setForm((current) => ({ ...current, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreedToTerms) return;
    onRegister({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      password: form.password,
      termsAccepted: true,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="register-first" className="block text-xs uppercase tracking-widest opacity-60 mb-2">First Name</label>
          <input id="register-first" name="firstName" type="text" required maxLength={50} autoComplete="given-name" value={form.firstName} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label htmlFor="register-last" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Last Name</label>
          <input id="register-last" name="lastName" type="text" required maxLength={50} autoComplete="family-name" value={form.lastName} onChange={handleChange} className={inputClass} />
        </div>
      </div>
      <div>
        <label htmlFor="register-email" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Email Address</label>
        <input id="register-email" name="email" type="email" required maxLength={254} autoComplete="email" placeholder="alex@example.com" value={form.email} onChange={handleChange} className={inputClass} />
      </div>
      <div>
        <label htmlFor="register-password" className="block text-xs uppercase tracking-widest opacity-60 mb-2">Password</label>
        <input id="register-password" name="password" type="password" required minLength={8} maxLength={128} autoComplete="new-password" placeholder="At least 8 characters" value={form.password} onChange={handleChange} className={inputClass} />
      </div>
      <TermsCheckbox id="register-terms" checked={agreedToTerms} onChange={setAgreedToTerms} />
      <button
        type="submit"
        disabled={isLoading || !agreedToTerms}
        className="w-full py-4 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#f7b43b] transition-colors disabled:opacity-50"
      >
        {isLoading ? "Creating account..." : "Create Account & Get 10% Off"} <Gift size={16} />
      </button>
    </form>
  );
};

const AUTH_COPY = {
  login: { title: "Sign In", intro: "Sign in with your email address to view your past orders, coupons, and account details." },
  register: { title: "Create Account", intro: "Join KrunchMate and get a 10% discount code for your next order." },
  forgot: { title: "Reset Password", intro: "Enter your account email address and we'll send you a link to choose a new password." },
};

const AuthPanel = ({ mode, onModeChange, onLogin, onRegister, isLoading, authError }) => (
  <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-32 pb-16 px-5 flex items-center justify-center">
    <div className="max-w-md w-full bg-black/20 border border-[#FFFBE5]/15 rounded-2xl p-8 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-display uppercase tracking-widest text-[#F6A81E]">{AUTH_COPY[mode].title}</h1>
        <p className="text-sm opacity-80 mt-2">{AUTH_COPY[mode].intro}</p>
      </div>

      {mode !== "forgot" && (
      <div className="grid grid-cols-2 gap-2 p-1 bg-black/30 rounded-full" role="tablist">
        {[
          { id: "login", label: "Sign In" },
          { id: "register", label: "Sign Up" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            onClick={() => onModeChange(tab.id)}
            className={`py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-colors ${
              mode === tab.id ? "bg-[#F6A81E] text-[#1D2528]" : "hover:bg-[#FFFBE5]/10"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {authError && (
        <div role="alert" className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200 text-xs text-center">
          {authError}
        </div>
      )}

      {mode === "login" && <LoginForm onLogin={onLogin} onForgotPassword={() => onModeChange("forgot")} isLoading={isLoading} />}
      {mode === "register" && <RegisterForm onRegister={onRegister} isLoading={isLoading} />}
      {mode === "forgot" && <ForgotPasswordForm onBack={() => onModeChange("login")} />}
    </div>
  </div>
);

const PromoBanner = ({ code, onDismiss }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // clipboard unavailable; the code is visible to copy by hand
    }
  };

  return (
    <div role="status" className="relative mb-10 p-6 lg:p-8 bg-gradient-to-br from-[#F6A81E]/30 to-[#F6A81E]/5 border-2 border-[#F6A81E] rounded-2xl">
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="absolute top-3 right-3 p-2 opacity-60 hover:opacity-100">
        <X size={18} />
      </button>
      <div className="flex flex-wrap items-center gap-6">
        <div className="p-3 bg-[#F6A81E]/20 text-[#F6A81E] rounded-2xl">
          <Gift size={32} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-display uppercase tracking-widest text-[#F6A81E] text-xl">Welcome! Here's 10% off</p>
          <p className="text-sm opacity-80 mt-1">Use this code at checkout with your account email. It works once.</p>
        </div>
        <div className="flex items-center gap-3 px-5 py-3 bg-black/30 border border-dashed border-[#F6A81E] rounded-xl">
          <span className="font-display text-xl tracking-widest text-[#F6A81E] break-all">{code}</span>
          <button type="button" onClick={handleCopy} aria-label="Copy discount code" className="p-1.5 rounded-lg hover:bg-[#FFFBE5]/10">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// onSave resolves to { ok, error }; the draft is kept on failure so nothing typed is lost
const AddressManager = ({ address, isLoading, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(address);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!isEditing) setDraft(address);
  }, [address, isEditing]);

  const handleChange = (e) => setDraft((current) => ({ ...current, [e.target.name]: e.target.value }));

  const startEditing = () => {
    setDraft(address);
    setSaveError("");
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError("");
    const result = await onSave(draft);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setSaveError(result.error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display uppercase tracking-widest text-sm opacity-60">Default Shipping Address</h3>
        {!isEditing && !isLoading && (
          <button type="button" onClick={startEditing} className="flex items-center gap-1 text-[#F6A81E] text-sm font-semibold hover:opacity-80 transition-opacity">
            <Pencil size={14} /> Edit
          </button>
        )}
      </div>

      {!isEditing ? (
        <div className="p-5 bg-black/20 border border-[#FFFBE5]/15 rounded-xl text-sm leading-relaxed min-h-[140px]">
          {isLoading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-4 bg-white/5 rounded w-2/3" />)}
            </div>
          ) : hasAddress(address) ? (
            <>
              <p className="font-bold text-base mb-1">{address.firstName} {address.lastName}</p>
              <p>{address.address}</p>
              <p>{address.city}</p>
              <p>{address.postcode}</p>
              <p>{formatCountry(address.country)}</p>
            </>
          ) : (
            <p className="opacity-50 italic">No default address saved.</p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSave} className="p-5 bg-black/20 border border-[#F6A81E]/40 rounded-xl space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="text" name="firstName" aria-label="First name" autoComplete="given-name" placeholder="First Name" required maxLength={50} value={draft.firstName} onChange={handleChange} className={smallInputClass} />
            <input type="text" name="lastName" aria-label="Last name" autoComplete="family-name" placeholder="Last Name" required maxLength={50} value={draft.lastName} onChange={handleChange} className={smallInputClass} />
          </div>
          <input type="text" name="address" aria-label="Street address" autoComplete="address-line1" placeholder="Street Address" required maxLength={200} value={draft.address} onChange={handleChange} className={smallInputClass} />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" name="city" aria-label="City" autoComplete="address-level2" placeholder="City" required maxLength={100} value={draft.city} onChange={handleChange} className={smallInputClass} />
            <input type="text" name="postcode" aria-label="Postcode" autoComplete="postal-code" placeholder="Postcode" required maxLength={10} value={draft.postcode} onChange={handleChange} className={smallInputClass} />
          </div>
          <p className="text-xs opacity-60">We currently deliver within the United Kingdom only.</p>

          {saveError && <p role="alert" className="text-red-200 text-xs">{saveError}</p>}

          <div className="flex gap-3 pt-3 mt-4 border-t border-[#FFFBE5]/10">
            <button type="submit" disabled={isSaving} className="flex-1 flex items-center justify-center gap-2 bg-[#F6A81E] text-[#1D2528] py-2.5 rounded-lg font-bold uppercase tracking-wider hover:bg-[#f7b43b] transition-colors text-sm disabled:opacity-50">
              <Save size={16} /> {isSaving ? "Saving..." : "Save Address"}
            </button>
            <button type="button" onClick={() => setIsEditing(false)} disabled={isSaving} aria-label="Cancel editing" className="px-4 flex items-center justify-center bg-white/5 border border-[#FFFBE5]/20 rounded-lg hover:bg-white/10 transition-colors">
              <X size={18} />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// ============================================================================
// ORDER TRACKING
// ============================================================================

// Stages come from the middleware: WooCommerce status plus Royal Mail tracking from Click & Drop
const TIMELINE_STEPS = [
  { key: "processing", label: "Processing", Icon: ClipboardCheck },
  { key: "dispatched", label: "Packed", detail: "Handed to Royal Mail", Icon: PackageCheck },
  { key: "in_transit", label: "In Transit", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: Home },
];
const STAGE_LABELS = { processing: "Processing", dispatched: "Packed", in_transit: "In transit", delivered: "Delivered" };
const DELAY_AFTER_DAYS = 5;
const DAY_MS = 24 * 60 * 60 * 1000;

const daysSince = (dateString) => {
  const time = dateString ? new Date(dateString).getTime() : NaN;
  return Number.isNaN(time) ? 0 : (Date.now() - time) / DAY_MS;
};

const orderStatusLabel = (order) => STAGE_LABELS[order.fulfilment] || order.status;

// Only an order still waiting to leave us can be called late: once it's with Royal Mail the store
// isn't told when it arrives, so their tracking is the source of truth from then on.
const isDelayed = (order) => order.fulfilment === "processing" && daysSince(order.dateCreated) > DELAY_AFTER_DAYS;

const REVIEW_EMAIL = "contact@krunchmate.com";

// Flavours in the order that the shop still sells, matched by WooCommerce product ID
const orderFlavours = (order) => {
  const byWooId = new Map(Object.values(FLAVOURS).map((flavour) => [flavour.wooId, flavour]));
  return order.items
    .map((item) => ({ flavour: byWooId.get(item.productId), quantity: item.quantity }))
    .filter((line) => line.flavour && line.quantity > 0);
};

// "Buy it again" puts the same pouches back in the basket; "Review" opens an email to us, so every
// review we publish comes from a real customer (fake reviews are banned under the DMCC Act 2024)
const DeliveredActions = ({ order }) => {
  const { addItem } = useCart();
  const lines = orderFlavours(order);
  if (lines.length === 0) return null;

  const buyAgain = () => lines.forEach(({ flavour, quantity }) => addItem(flavour.id, quantity));
  const reviewLink = (flavour) =>
    `mailto:${REVIEW_EMAIL}?subject=${encodeURIComponent(`My review of ${flavour.name} (order #${order.number})`)}`;

  return (
    <div className="mt-4 flex flex-wrap gap-3">
      <button
        type="button"
        onClick={buyAgain}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#f7b43b] transition-colors"
      >
        <ShoppingBag size={14} /> Buy it again
      </button>
      {lines.map(({ flavour }) => (
        <a
          key={flavour.id}
          href={reviewLink(flavour)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-white/20 transition-colors"
        >
          <Star size={14} /> Review {lines.length > 1 ? flavour.name : "this flavour"}
        </a>
      ))}
    </div>
  );
};

const OrderTimeline = ({ order }) => {
  const activeIndex = TIMELINE_STEPS.findIndex((step) => step.key === order.fulfilment);
  if (activeIndex === -1) return null;
  const stepDates = { processing: order.dateCreated, dispatched: order.dateCompleted };

  return (
    <section aria-label="Delivery progress" className="p-5 lg:p-6 bg-black/20 border border-[#FFFBE5]/10 rounded-2xl space-y-6">
      {isDelayed(order) && (
        <div role="alert" className="flex items-start gap-3 p-4 bg-[#F6A81E]/15 border border-[#F6A81E]/40 rounded-xl text-sm">
          <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[#F6A81E]" />
          <p>
            We sincerely apologise for the delay! Your order is taking longer than expected. Please track via Royal
            Mail or <Link to="/contact" className="underline text-[#F6A81E]">contact support</Link>.
          </p>
        </div>
      )}

      <ol>
        {TIMELINE_STEPS.map((step, index) => {
          const isDone = index <= activeIndex;
          const isCurrent = index === activeIndex;
          const isLast = index === TIMELINE_STEPS.length - 1;
          const date = stepDates[step.key];
          return (
            <li key={step.key} className="relative flex gap-4 pb-7 last:pb-0" aria-current={isCurrent ? "step" : undefined}>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute left-5 top-10 bottom-0 w-1 -translate-x-1/2 ${index < activeIndex ? "bg-[#F6A81E]" : "bg-[#FFFBE5]/15"}`}
                />
              )}
              <span
                className={`relative z-10 flex items-center justify-center w-10 h-10 shrink-0 rounded-full border-2 ${
                  isDone ? "bg-[#F6A81E] border-[#F6A81E] text-[#1D2528]" : "bg-[#082D33] border-[#FFFBE5]/25 text-[#FFFBE5]/40"
                } ${isCurrent ? "ring-4 ring-[#F6A81E]/30" : ""}`}
              >
                <step.Icon size={18} />
              </span>
              <div className="pt-1.5 min-w-0">
                <p className={`text-sm font-bold uppercase tracking-wider ${isDone ? "text-[#FFFBE5]" : "text-[#FFFBE5]/40"}`}>
                  {step.label}
                  <span className="sr-only">{isCurrent ? " (current stage)" : isDone ? " (done)" : " (to come)"}</span>
                </p>
                {isDone && (step.detail || date) && (
                  <p className="mt-1 text-xs opacity-60">
                    {[step.detail, date ? formatDate(date) : ""].filter(Boolean).join(" · ")}
                  </p>
                )}

                {step.key === "in_transit" && isDone && order.tracking && (
                  <div className="mt-3 text-sm">
                    <p className="opacity-60 text-xs">
                      {order.tracking.carrier} tracking number{" "}
                      <span className="font-bold tracking-wider opacity-100 text-[#FFFBE5]">{order.tracking.number}</span>
                    </p>
                    {order.tracking.url && (
                      <a
                        href={order.tracking.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#f7b43b] transition-colors"
                      >
                        Track with Royal Mail <ExternalLink size={14} />
                        <span className="sr-only">(opens in a new tab)</span>
                      </a>
                    )}
                  </div>
                )}

                {step.key === "delivered" && isDone && <DeliveredActions order={order} />}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const OrderDetail = ({ order, onBack }) => (
  <div className="space-y-6">
    <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm font-semibold opacity-70 hover:opacity-100 transition-opacity text-[#F6A81E]">
      <ArrowLeft size={16} /> Back to Orders
    </button>

    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#FFFBE5]/15 pb-4">
      <div>
        <h2 className="text-2xl lg:text-3xl font-display uppercase tracking-wider text-[#F6A81E]">Order #{order.number}</h2>
        <p className="opacity-70 mt-1">Placed on {formatDate(order.dateCreated)}</p>
      </div>
      <span className="px-4 py-1.5 text-sm uppercase tracking-wider bg-[#FFFBE5]/10 rounded-full">{orderStatusLabel(order)}</span>
    </div>

    <OrderTimeline order={order} />

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2 space-y-4">
        <h3 className="font-display uppercase tracking-widest text-sm opacity-60 mb-2">Items Purchased</h3>
        {order.items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center p-4 bg-black/20 border border-[#FFFBE5]/10 rounded-xl">
            <div>
              <p className="font-bold">{item.name}</p>
              <p className="text-sm opacity-70">Qty: {item.quantity}</p>
            </div>
            <p className="font-bold">{formatPence(item.totalPence)}</p>
          </div>
        ))}
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="font-display uppercase tracking-widest text-sm opacity-60 mb-4">Order Summary</h3>
          <div className="space-y-3 p-5 bg-black/20 border border-[#FFFBE5]/10 rounded-xl text-sm">
            <div className="flex justify-between">
              <span className="opacity-70">Subtotal</span>
              <span>{formatPence(order.subtotalPence)}</span>
            </div>
            {order.discountPence > 0 && (
              <div className="flex justify-between">
                <span className="opacity-70">Discount</span>
                <span>−{formatPence(order.discountPence)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="opacity-70">Shipping</span>
              <span>{order.shippingPence === 0 ? "Free" : formatPence(order.shippingPence)}</span>
            </div>
            <div className="border-t border-[#FFFBE5]/15 pt-3 mt-3 flex justify-between font-bold text-base text-[#F6A81E]">
              <span>Total</span>
              <span>{formatPence(order.totalPence)}</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-display uppercase tracking-widest text-sm opacity-60 mb-4">Shipping Address</h3>
          <div className="p-5 bg-black/20 border border-[#FFFBE5]/10 rounded-xl text-sm leading-relaxed">
            <p>{order.shipping.firstName} {order.shipping.lastName}</p>
            <p>{order.shipping.address}</p>
            <p>{order.shipping.city}{order.shipping.postcode ? `, ${order.shipping.postcode}` : ""}</p>
            <p>{formatCountry(order.shipping.country)}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CouponList = ({ coupons }) =>
  coupons.length === 0 ? (
    <p className="opacity-70">You don't have any discount codes yet.</p>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {coupons.map((coupon) => (
        <div key={coupon.code} className={`relative p-6 bg-gradient-to-br from-[#F6A81E]/20 to-transparent border border-[#F6A81E]/40 rounded-2xl overflow-hidden ${coupon.status === "used" ? "opacity-50" : ""}`}>
          <p className="font-display text-2xl text-[#F6A81E] tracking-widest break-all">{coupon.code}</p>
          <p className="font-semibold mt-2">{coupon.description}</p>
          <p className="text-xs opacity-60 mt-4">{coupon.status === "used" ? "Already used" : "Single use · enter at checkout"}</p>
        </div>
      ))}
    </div>
  );

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MyAccount() {
  // --- Auth State ---
  // The session is shared through AuthContext, so signing in here updates the whole site
  const { token, user, signIn, signOut } = useAuth();
  const userEmail = user?.email || "";
  const [authMode, setAuthMode] = useState("login");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState("");
  const [welcomeCoupon, setWelcomeCoupon] = useState("");

  // --- UI & Data State ---
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [savedAddress, setSavedAddress] = useState(EMPTY_ADDRESS);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [coupons, setCoupons] = useState([]);

  const handleLogout = useCallback((message = "") => {
    signOut();
    setOrders([]);
    setSelectedOrder(null);
    setSavedAddress(EMPTY_ADDRESS);
    setCoupons([]);
    setWelcomeCoupon("");
    setAuthError(message);
  }, [signOut]);

  // --- Live updates: the middleware pushes an order whenever WooCommerce reports a change to it ---
  useEffect(() => {
    if (!token) return undefined;
    return subscribeToOrderUpdates({
      url: `${MIDDLEWARE_URL}/api/account/orders/stream`,
      token,
      onOrder: (update) => {
        setOrders((current) => [update, ...current.filter((order) => order.id !== update.id)].sort((a, b) => b.id - a.id));
        setSelectedOrder((current) => (current && current.id === update.id ? update : current));
      },
    });
  }, [token]);

  // --- Orders (WooCommerce won't show orders to a customer token, so the middleware reads them) ---
  useEffect(() => {
    if (!token || !userEmail) return undefined;

    let isMounted = true;
    const fetchOrders = async () => {
      setIsLoadingOrders(true);
      setOrdersError("");
      try {
        const { ok, status, data } = await middlewareRequest(
          `/api/account/orders?customer_email=${encodeURIComponent(userEmail)}`,
          { token }
        );
        if (!isMounted) return;
        if (status === 401) {
          handleLogout(safeMessage(data.error, "Your session has expired. Please sign in again."));
          return;
        }
        if (ok && Array.isArray(data.orders)) {
          setOrders(data.orders);
        } else {
          setOrdersError(safeMessage(data.error, "We couldn't load your orders right now. Please try again."));
        }
      } catch (err) {
        console.error("Failed to fetch orders:", err);
        if (isMounted) setOrdersError("Server communication failed. Please check your connection and try again.");
      } finally {
        if (isMounted) setIsLoadingOrders(false);
      }
    };

    fetchOrders();
    return () => {
      isMounted = false;
    };
  }, [token, userEmail, handleLogout]);

  // --- Saved address and discount codes (from WooCommerce via the middleware) ---
  useEffect(() => {
    if (!token) return undefined;
    let isCurrent = true;

    const loadAccountData = async () => {
      setIsLoadingAddress(true);
      try {
        const [addressResult, couponResult] = await Promise.all([
          middlewareRequest(`/api/account/address?email=${encodeURIComponent(userEmail)}`, { token }),
          middlewareRequest("/api/account/coupons", { token }),
        ]);
        if (!isCurrent) return;

        if (addressResult.status === 401 || couponResult.status === 401) {
          handleLogout(safeMessage(addressResult.data.error, "Your session has expired. Please sign in again."));
          return;
        }
        if (addressResult.ok && addressResult.data.address) {
          setSavedAddress({ ...EMPTY_ADDRESS, ...addressResult.data.address });
        }
        if (couponResult.ok && Array.isArray(couponResult.data.coupons)) {
          setCoupons(couponResult.data.coupons);
        }
      } catch (err) {
        console.error("Failed to load account details:", err);
      } finally {
        if (isCurrent) setIsLoadingAddress(false);
      }
    };

    loadAccountData();
    return () => {
      isCurrent = false;
    };
  }, [token, userEmail, handleLogout]);

  // --- Handlers ---
  const completeSignIn = ({ token: newToken, email }) => {
    signIn({ token: newToken, email });
  };

  const handleLogin = async (email, password) => {
    setAuthError("");
    setIsAuthenticating(true);
    try {
      completeSignIn(await requestStoreToken(email, password));
    } catch (err) {
      setAuthError(err instanceof TypeError
        ? "Server communication failed. Please check your connection and try again."
        : err.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleRegister = async (details) => {
    setAuthError("");
    setIsAuthenticating(true);
    try {
      const { ok, data } = await middlewareRequest("/api/register", { method: "POST", body: details });
      if (!ok || data.success !== true) {
        setAuthError(safeMessage(data.error, "We couldn't create your account. Please try again."));
        return;
      }

      if (typeof data.coupon === "string" && data.coupon) {
        writeStorage(WELCOME_COUPON_KEY, data.coupon);
        setWelcomeCoupon(data.coupon);
      }

      // Sign straight in with the details just registered
      try {
        completeSignIn(await requestStoreToken(details.email, details.password));
      } catch {
        setAuthMode("login");
        setAuthError("Account created successfully! Please sign in.");
      }
    } catch {
      setAuthError("Server communication failed. Please check your connection and try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSaveAddress = async (draft) => {
    const shippingAddress = {
      firstName: draft.firstName,
      lastName: draft.lastName,
      address: draft.address,
      city: draft.city,
      postcode: draft.postcode,
    };
    try {
      const { ok, status, data } = await middlewareRequest("/api/update-address", {
        method: "POST",
        token,
        body: { customerEmail: userEmail, shippingAddress, billingAddress: shippingAddress },
      });
      if (status === 401) {
        handleLogout(safeMessage(data.error, "Your session has expired. Please sign in again."));
        return { ok: false, error: "" };
      }
      if (!ok || data.success !== true || !data.address) {
        return { ok: false, error: safeMessage(data.error, "We couldn't save your address. Please try again.") };
      }
      setSavedAddress({ ...EMPTY_ADDRESS, ...data.address });
      return { ok: true };
    } catch {
      return { ok: false, error: "Server communication failed. Please check your connection and try again." };
    }
  };

  const handleChangePassword = async (currentPassword, newPassword) => {
    try {
      const { ok, status, data } = await middlewareRequest("/api/account/change-password", {
        method: "POST",
        token,
        body: { currentPassword, newPassword },
      });
      if (status === 401) {
        handleLogout(safeMessage(data.error, "Your session has expired. Please sign in again."));
        return { ok: false, error: "" };
      }
      if (!ok || data.success !== true) {
        return { ok: false, error: safeMessage(data.error, "We couldn't update your password. Please try again.") };
      }
      return { ok: true };
    } catch {
      return { ok: false, error: NETWORK_ERROR_MESSAGE };
    }
  };

  const changeTab = useCallback((tab) => {
    setActiveTab(tab);
    setSelectedOrder(null);
  }, []);

  const changeAuthMode = useCallback((mode) => {
    setAuthMode(mode);
    setAuthError("");
  }, []);

  // --- Render Unauthenticated ---
  if (!token) {
    return (
      <AuthPanel
        mode={authMode}
        onModeChange={changeAuthMode}
        onLogin={handleLogin}
        onRegister={handleRegister}
        isLoading={isAuthenticating}
        authError={authError}
      />
    );
  }

  // The middleware already falls back to the latest order's address when no default is saved
  const displayAddress = hasAddress(savedAddress) ? savedAddress : orders[0]?.shipping || EMPTY_ADDRESS;

  // A fresh sign-up's code shows immediately, before the coupon lookup returns
  const couponList = coupons.length > 0 || !welcomeCoupon
    ? coupons
    : [{ code: welcomeCoupon, description: "10% off your order", status: "available" }];

  // --- Render Authenticated Dashboard ---
  return (
    <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-24 pb-16 px-5 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10 border-b border-[#FFFBE5]/15 pb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-5xl font-display uppercase tracking-[0.06em] text-[#F6A81E]">My Account</h1>
            <p className="mt-2 text-lg opacity-80 break-all">Welcome back, {userEmail}.</p>
          </div>
          <button type="button" onClick={() => handleLogout()} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold transition-colors">
            <LogOut size={16} /> Log Out
          </button>
        </div>

        {welcomeCoupon && <PromoBanner code={welcomeCoupon} onDismiss={() => setWelcomeCoupon("")} />}

        <div className="flex flex-col md:flex-row gap-10">
          <aside className="w-full md:w-64 shrink-0">
            <nav className="flex flex-col gap-2" aria-label="Account sections">
              {[
                { id: "orders", icon: Package, label: "Order History" },
                { id: "coupons", icon: Ticket, label: "My Coupons" },
                { id: "settings", icon: User, label: "Account Details" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeTab(tab.id)}
                  aria-current={activeTab === tab.id ? "page" : undefined}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left font-bold ${
                    activeTab === tab.id ? "bg-[#FFFBE5]/10 text-[#F6A81E]" : "hover:bg-[#FFFBE5]/5"
                  }`}
                >
                  <tab.icon size={20} /> {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          <main className="flex-1 min-w-0 bg-[#FFFBE5]/5 border border-[#FFFBE5]/15 rounded-2xl p-6 lg:p-10">
            {activeTab === "orders" && !selectedOrder && (
              <div>
                <h2 className="text-2xl font-display uppercase tracking-wider mb-6">Past Orders</h2>
                {isLoadingOrders ? (
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-white/5 rounded-xl w-full" />)}
                  </div>
                ) : ordersError ? (
                  <p role="alert" className="text-red-200 text-sm">{ordersError}</p>
                ) : orders.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {orders.map((order) => (
                      <button
                        key={order.id}
                        type="button"
                        onClick={() => setSelectedOrder(order)}
                        className="group flex flex-wrap items-center justify-between gap-4 p-5 bg-black/20 border border-[#FFFBE5]/10 rounded-xl text-left hover:border-[#F6A81E]/40 transition-colors w-full"
                      >
                        <div>
                          <p className="font-bold text-[#F6A81E] text-lg">#{order.number}</p>
                          <p className="text-sm opacity-70 mt-1">{formatDate(order.dateCreated)} · {order.items.length} item(s)</p>
                        </div>
                        <div className="flex items-center gap-4 text-right">
                          <div>
                            <p className="font-bold">{formatPence(order.totalPence)}</p>
                            <span className="inline-block mt-1 px-3 py-1 text-xs uppercase tracking-wider bg-[#FFFBE5]/10 rounded-full">{orderStatusLabel(order)}</span>
                          </div>
                          <ChevronRight className="opacity-50 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="opacity-70">You haven't placed any orders yet.</p>
                )}
              </div>
            )}

            {activeTab === "orders" && selectedOrder && (
              <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} />
            )}

            {activeTab === "coupons" && (
              <div>
                <h2 className="text-2xl font-display uppercase tracking-wider mb-6">Available Coupons</h2>
                <CouponList coupons={couponList} />
              </div>
            )}

            {activeTab === "settings" && (
              <div>
                <h2 className="text-2xl font-display uppercase tracking-wider mb-6">Account Details</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div className="space-y-4">
                    <h3 className="font-display uppercase tracking-widest text-sm opacity-60 mb-4">Profile Information</h3>
                    <div>
                      <span className="block text-xs uppercase tracking-widest opacity-60 mb-1">Email</span>
                      <div className="p-3 bg-black/20 border border-[#FFFBE5]/15 rounded-lg break-all">{userEmail}</div>
                    </div>
                    <div className="pt-6">
                      <ChangePasswordForm onSubmit={handleChangePassword} />
                    </div>
                  </div>
                  <AddressManager address={displayAddress} isLoading={isLoadingAddress} onSave={handleSaveAddress} />
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// /account/reset?key=...&login=... (the link in the password reset email)
export function ResetPasswordPage() {
  const navigate = useNavigate();
  // Read once, then removed from the address bar so the key doesn't linger in history
  const [link] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { key: params.get("key") || "", login: params.get("login") || "" };
  });
  const [password, setPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (window.location.search) navigate("/account/reset", { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSaving(true);
    try {
      const { ok, data } = await middlewareRequest("/api/account/reset-password", {
        method: "POST",
        body: { key: link.key, login: link.login, password },
      });
      if (ok && data.success === true) {
        setDone(true);
      } else {
        setError(safeMessage(data.error, "We couldn't reset your password. Please try again."));
      }
    } catch {
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  const linkIsUsable = Boolean(link.key && link.login);

  return (
    <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-32 pb-16 px-5 flex items-center justify-center">
      <div className="max-w-md w-full bg-black/20 border border-[#FFFBE5]/15 rounded-2xl p-8 space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-display uppercase tracking-widest text-[#F6A81E]">Choose a New Password</h1>
        </div>

        {done ? (
          <div className="space-y-4 text-center">
            <p role="status" className="p-3 bg-[#F6A81E]/15 border border-[#F6A81E]/40 rounded-xl text-sm">
              Your password has been reset. You can now sign in.
            </p>
            <Link to="/account" className={submitButtonClass}>Sign In <Lock size={16} /></Link>
          </div>
        ) : !linkIsUsable ? (
          <div className="space-y-4 text-center">
            <p role="alert" className="text-sm opacity-80">This reset link is incomplete. Please request a new one from the sign-in page.</p>
            <Link to="/account" className="text-sm underline opacity-80 hover:opacity-100">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div role="alert" className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200 text-xs text-center">{error}</div>
            )}
            <div>
              <label htmlFor="reset-password" className="block text-xs uppercase tracking-widest opacity-60 mb-2">New Password</label>
              <input id="reset-password" type="password" required minLength={8} maxLength={128} autoComplete="new-password" placeholder="At least 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
            </div>
            <button type="submit" disabled={isSaving} className={submitButtonClass}>
              {isSaving ? "Saving..." : "Set New Password"} <KeyRound size={16} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
